import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { BodyPart } from './timelineStore';
import type { EasingFunction } from './puppetStore';

export interface CameraPosition {
  x: number;
  y: number;
  z: number;
}

export interface PanelSizes {
  sidebarWidth?: number;
  previewHeight?: number;
  timelineHeight?: number;
}

/**
 * Zoom constants for timeline
 * - Default: 50 pixels per second (balanced view)
 * - Min: 0.5 pixels per second (1 hour = 1800px, fits in viewport)
 * - Max: 100 pixels per second (detailed view)
 * - Step: Logarithmic stepping handled by zoom functions
 */
export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 100;
export const ZOOM_DEFAULT = 50;

/**
 * Tool data stored in clipboard with relative timing offsets
 * Used for copy/paste and group creation
 */
export interface ClipboardTool {
  toolId: string;
  bodyPart: BodyPart;
  duration: number;
  targetRotation: number;
  easing: EasingFunction;
  /** Time offset relative to earliest tool (earliest = 0) */
  relativeStartTime: number;
  /** Track offset relative to first track used (1-indexed) */
  relativeTrackNumber: number;
  /** Target position for Reset Position tool only */
  targetPosition?: 'start' | 'dock';
}

/**
 * Clipboard data structure for copy/paste functionality
 * Stores tools with relative timing to preserve group structure
 */
export interface ClipboardData {
  tools: ClipboardTool[];
}

interface UiState {
  /**
   * Set of selected tool IDs for multi-select
   * Uses Set<string> for O(1) lookups
   */
  selectedToolIds: Set<string>;
  /**
   * @deprecated Use selectedToolIds.has(id) instead
   * Kept for backward compatibility during migration
   */
  selectedToolBlockId: string | null;
  sidebarWidth: number;
  previewHeight: number;
  timelineHeight: number;
  cameraPosition: CameraPosition;
  /** Timeline zoom level in pixels per second (50-300) */
  pixelsPerSecond: number;
  /** Whether the configurator modal is open */
  configuratorOpen: boolean;
  /** Clipboard data for copy/paste operations */
  clipboard: ClipboardData | null;
  /**
   * ID of group currently being edited in group edit mode.
   * When set, the timeline shows the group's internal tools instead of main timeline.
   * Null means we're viewing the main timeline.
   */
  editingGroupId: string | null;
  /**
   * Whether rubber-band selection is currently active.
   * When true, tool blocks should disable hover effects and resize cursors.
   */
  isRubberBandSelecting: boolean;
}

interface UiActions {
  // Multi-select actions
  /** Add a tool ID to the selection Set */
  addToSelection: (id: string) => void;
  /** Remove a tool ID from the selection Set */
  removeFromSelection: (id: string) => void;
  /** Toggle a tool ID in/out of selection */
  toggleSelection: (id: string) => void;
  /** Clear all selections */
  clearSelection: () => void;
  /** Replace entire selection with array of IDs */
  setSelection: (ids: string[]) => void;

  // Clipboard actions
  /** Copy tools to clipboard with relative timing */
  copyToClipboard: (data: ClipboardData) => void;
  /** Clear clipboard data */
  clearClipboard: () => void;

  // Legacy single-select (for backward compatibility)
  /**
   * @deprecated Use toggleSelection instead
   */
  selectToolBlock: (id: string) => void;
  /**
   * @deprecated Use clearSelection instead
   */
  deselectToolBlock: () => void;

  updatePanelSizes: (sizes: PanelSizes) => void;
  updateCameraPosition: (position: Partial<CameraPosition>) => void;
  /** Set zoom level in pixels per second (clamped to 50-300 range) */
  setPixelsPerSecond: (value: number) => void;
  /** Zoom in by one step (25px/s) */
  zoomIn: () => void;
  /** Zoom out by one step (25px/s) */
  zoomOut: () => void;
  /** Open the puppet configurator modal */
  openConfigurator: () => void;
  /** Close the puppet configurator modal */
  closeConfigurator: () => void;
  /** Enter group edit mode for editing a group's internal tools */
  enterGroupEditMode: (groupId: string) => void;
  /** Exit group edit mode and return to main timeline */
  exitGroupEditMode: () => void;
  /** Set rubber-band selection active state */
  setRubberBandSelecting: (isSelecting: boolean) => void;
}

type UiStore = UiState & UiActions;

// Default camera position for front-facing view
const DEFAULT_CAMERA_POSITION: CameraPosition = {
  x: 0,
  y: 1.5,
  z: 5,
};

// Default panel sizes in pixels
const DEFAULT_SIDEBAR_WIDTH = 280;
const DEFAULT_PREVIEW_HEIGHT = 600;
const DEFAULT_TIMELINE_HEIGHT = 400;

/**
 * Clamp a value between min and max bounds
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export const useUiStore = create<UiStore>()(
  immer((set) => ({
    // Initial state
    selectedToolIds: new Set<string>(),
    selectedToolBlockId: null, // Deprecated, kept for migration
    sidebarWidth: DEFAULT_SIDEBAR_WIDTH,
    previewHeight: DEFAULT_PREVIEW_HEIGHT,
    timelineHeight: DEFAULT_TIMELINE_HEIGHT,
    cameraPosition: DEFAULT_CAMERA_POSITION,
    pixelsPerSecond: ZOOM_DEFAULT,
    configuratorOpen: false,
    clipboard: null,
    editingGroupId: null,
    isRubberBandSelecting: false,

    // Multi-select actions
    addToSelection: (id) =>
      set((state) => {
        // With immer, we need to create a new Set to trigger reactivity
        state.selectedToolIds = new Set([...state.selectedToolIds, id]);
        // Also update legacy field for backward compatibility
        state.selectedToolBlockId = id;
      }),

    removeFromSelection: (id) =>
      set((state) => {
        const newSet = new Set(state.selectedToolIds);
        newSet.delete(id);
        state.selectedToolIds = newSet;
        // Update legacy field if it was the removed one
        if (state.selectedToolBlockId === id) {
          state.selectedToolBlockId =
            newSet.size > 0 ? Array.from(newSet)[0] : null;
        }
      }),

    toggleSelection: (id) =>
      set((state) => {
        const newSet = new Set(state.selectedToolIds);
        if (newSet.has(id)) {
          newSet.delete(id);
          // Update legacy field
          state.selectedToolBlockId =
            newSet.size > 0 ? Array.from(newSet)[0] : null;
        } else {
          newSet.add(id);
          // Update legacy field
          state.selectedToolBlockId = id;
        }
        state.selectedToolIds = newSet;
      }),

    clearSelection: () =>
      set((state) => {
        state.selectedToolIds = new Set();
        state.selectedToolBlockId = null;
      }),

    setSelection: (ids) =>
      set((state) => {
        state.selectedToolIds = new Set(ids);
        // Update legacy field to first selected
        state.selectedToolBlockId = ids.length > 0 ? ids[0] : null;
      }),

    // Clipboard actions
    copyToClipboard: (data) =>
      set((state) => {
        state.clipboard = data;
      }),

    clearClipboard: () =>
      set((state) => {
        state.clipboard = null;
      }),

    // Legacy single-select actions (for backward compatibility)
    selectToolBlock: (id) =>
      set((state) => {
        // Legacy behavior: single select clears others
        state.selectedToolIds = new Set([id]);
        state.selectedToolBlockId = id;
      }),

    deselectToolBlock: () =>
      set((state) => {
        state.selectedToolIds = new Set();
        state.selectedToolBlockId = null;
      }),

    updatePanelSizes: (sizes) =>
      set((state) => {
        if (sizes.sidebarWidth !== undefined) {
          state.sidebarWidth = sizes.sidebarWidth;
        }
        if (sizes.previewHeight !== undefined) {
          state.previewHeight = sizes.previewHeight;
        }
        if (sizes.timelineHeight !== undefined) {
          state.timelineHeight = sizes.timelineHeight;
        }
      }),

    updateCameraPosition: (position) =>
      set((state) => {
        if (position.x !== undefined) {
          state.cameraPosition.x = position.x;
        }
        if (position.y !== undefined) {
          state.cameraPosition.y = position.y;
        }
        if (position.z !== undefined) {
          state.cameraPosition.z = position.z;
        }
      }),

    setPixelsPerSecond: (value) =>
      set((state) => {
        state.pixelsPerSecond = clamp(value, ZOOM_MIN, ZOOM_MAX);
      }),

    zoomIn: () =>
      set((state) => {
        // Logarithmic zoom: multiply by 1.25 for smooth scaling across wide range
        state.pixelsPerSecond = clamp(
          state.pixelsPerSecond * 1.25,
          ZOOM_MIN,
          ZOOM_MAX
        );
      }),

    zoomOut: () =>
      set((state) => {
        // Logarithmic zoom: divide by 1.25 for smooth scaling across wide range
        state.pixelsPerSecond = clamp(
          state.pixelsPerSecond / 1.25,
          ZOOM_MIN,
          ZOOM_MAX
        );
      }),

    openConfigurator: () =>
      set((state) => {
        state.configuratorOpen = true;
      }),

    closeConfigurator: () =>
      set((state) => {
        state.configuratorOpen = false;
      }),

    enterGroupEditMode: (groupId) =>
      set((state) => {
        state.editingGroupId = groupId;
        // Clear selection when entering edit mode
        state.selectedToolIds = new Set();
        state.selectedToolBlockId = null;
      }),

    exitGroupEditMode: () =>
      set((state) => {
        state.editingGroupId = null;
        // Clear selection when exiting edit mode
        state.selectedToolIds = new Set();
        state.selectedToolBlockId = null;
      }),

    setRubberBandSelecting: (isSelecting) =>
      set((state) => {
        state.isRubberBandSelecting = isSelecting;
      }),
  }))
);
